import ActionCable from 'actioncable';
import { useEffect, useState } from 'react';
import { useOptionalSession } from 'src/contexts/SessionContext';

let sharedConsumer: ActionCable.Cable | null = null;
let consumerRefCount = 0;

const getSharedConsumer = (): ActionCable.Cable => {
  if (!sharedConsumer) {
    sharedConsumer = ActionCable.createConsumer('/cable/');
  }
  consumerRefCount++;
  return sharedConsumer;
};

const releaseSharedConsumer = () => {
  consumerRefCount--;
  if (consumerRefCount <= 0) {
    sharedConsumer?.disconnect();
    sharedConsumer = null;
    consumerRefCount = 0;
  }
};

/**
 * Returns a shared ActionCable consumer that is reference-counted across all
 * callers. The underlying WebSocket connection is created once and reused by
 * every hook/component that needs ActionCable. It is disconnected only when
 * the last consumer is released.
 *
 * Typical usage:
 *   const cable = useCable();
 *   useEffect(() => {
 *     if (!cable) return;
 *     const sub = cable.subscriptions.create(params, callbacks);
 *     return () => { sub.unsubscribe(); };
 *   }, [cable, ...deps]);
 */
export const useCable = (): ActionCable.Cable | null => {
  const { user } = useOptionalSession();
  const [cable, setCable] = useState<ActionCable.Cable | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setCable(null);
      return;
    }

    const consumer = getSharedConsumer();
    setCable(consumer);

    return () => {
      releaseSharedConsumer();
      setCable(null);
    };
  }, [user?.id]);

  return cable;
};
